/**
 * FeedbackStep - Collect health priorities and optional feedback
 *
 * Used for both continue and cancel flows to understand user preferences.
 */

import { useState, useCallback } from 'react';
import type { HealthPriorities, FlowPath } from '../useMultiDiseaseFeedback';
import { DiseaseOptionCard } from './DiseaseOptionCard';

const DISEASE_OPTIONS = [
  { value: 'cancer' as const, label: 'Cancer' },
  { value: 'heartDisease' as const, label: 'Heart disease' },
  { value: 'dementia' as const, label: 'Dementia' },
  { value: 'diabetes' as const, label: 'Diabetes' },
];

interface FeedbackStepProps {
  flowPath: FlowPath;
  onSubmit: (priorities: HealthPriorities, additionalFeedback?: string) => Promise<void>;
  isSubmitting: boolean;
  error: string | null;
}

export function FeedbackStep({
  flowPath,
  onSubmit,
  isSubmitting,
  error,
}: FeedbackStepProps) {
  const [selectedPriorities, setSelectedPriorities] = useState<HealthPriorities>({
    cancer: false,
    heartDisease: false,
    dementia: false,
    diabetes: false,
  });
  const [additionalFeedback, setAdditionalFeedback] = useState('');

  const hasSelection = Object.values(selectedPriorities).some(Boolean);

  const handleToggle = useCallback((key: keyof HealthPriorities) => {
    setSelectedPriorities((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  const handleSubmit = useCallback(async () => {
    await onSubmit(selectedPriorities, additionalFeedback || undefined);
  }, [onSubmit, selectedPriorities, additionalFeedback]);

  const ctaText =
    flowPath === 'continue' ? 'Continue' : 'Continue to cancel';

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-xl font-bold text-catch-dark-blue-90">
          Quick question before you continue. Which health risks are most relevant to you right now?
        </h2>
        <p className="mt-2 text-catch-slate">Select all that apply</p>
      </div>

      {/* Disease options */}
      <div className="flex flex-col gap-3">
        {DISEASE_OPTIONS.map((option) => (
          <DiseaseOptionCard
            key={option.value}
            label={option.label}
            isSelected={selectedPriorities[option.value]}
            onToggle={() => handleToggle(option.value)}
          />
        ))}
      </div>

      {/* Additional feedback textarea */}
      <div className="flex flex-col gap-2">
        <label htmlFor="additional-feedback" className="text-sm font-medium text-catch-slate">
          Want to share what&apos;s driving this? (optional)
        </label>
        <textarea
          id="additional-feedback"
          value={additionalFeedback}
          onChange={(e) => setAdditionalFeedback(e.target.value)}
          placeholder="Tell us more"
          className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-catch-dark-blue-90 placeholder-gray-400 focus:border-catch-teal focus:outline-none focus:ring-1 focus:ring-catch-teal"
          rows={3}
        />
      </div>

      {/* Error message */}
      {error && (
        <p className="text-center text-sm text-catch-danger">{error}</p>
      )}

      {/* Submit CTA */}
      <button
        onClick={handleSubmit}
        disabled={!hasSelection || isSubmitting}
        className="flex w-full items-center justify-center rounded-full bg-catch-avocado-400 px-5 py-4 text-sm font-bold text-catch-dark-blue-90 transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isSubmitting ? 'Submitting...' : ctaText}
      </button>
    </div>
  );
}

export default FeedbackStep;
